import type { ToastType } from '@/components/ui/UiToast.vue'
import { reactive } from 'vue'

export interface Toast {
  id: string
  type: ToastType
  message: string
  closable: boolean
  duration: number | null
}

export interface Options {
  type?: ToastType
  closable?: boolean
  /** milliseconds, null = infinite. Auto-computed from content if omitted. */
  duration?: number | null
}

const toasts = reactive<Toast[]>([])

function computeDuration(options: Options): number | null {
  if (options.duration !== undefined) {
    return options.duration
  }
  if (options.closable) {
    return 5000
  }
  return 3000
}

export function useToast() {
  function add(message: string, options: Options = {}): string {
    const id = Math.random().toString(36).slice(2, 9)
    const closable = options.closable ?? (options.type === 'error' || options.duration === null)
    const duration = computeDuration({ ...options, closable })

    toasts.push({
      id,
      type: options.type ?? 'info',
      message,
      closable,
      duration,
    })

    return id
  }

  function remove(id: string): void {
    const index = toasts.findIndex((n) => n.id === id)
    if (index >= 0) {
      toasts.splice(index, 1)
    }
  }

  function success(message: string, options: Omit<Options, 'type'> = {}): string {
    return add(message, { ...options, type: 'success' })
  }

  function error(message: string, options: Omit<Options, 'type'> = {}): string {
    return add(message, { closable: true, ...options, type: 'error' })
  }

  function warning(message: string, options: Omit<Options, 'type'> = {}): string {
    return add(message, { ...options, type: 'warning' })
  }

  function info(message: string, options: Omit<Options, 'type'> = {}): string {
    return add(message, { ...options, type: 'info' })
  }

  return { toasts, add, remove, success, error, warning, info }
}
